import {
  MaxRecordsReachedWarning,
  MobileDataTable,
  useAccordionFilterBuilder,
  useBlotterTable,
  useDateRangeFilter,
  type Order,
} from '@talos/kyoko';
import { useCallback } from 'react';
import { orderTimestampFields } from '../../../../Blotters/Orders/orderTimestampFields';
import { useFilteredOrdersBlotter } from '../../../../Blotters/Orders/useFilteredOrdersBlotter';
import type { BlotterTableOrderFilter } from '../../../../Blotters/Orders/useOrderFilter';
import { MobileBlotterToolbar } from '../MobileBlotterToolbar';
import { MobileOrderCard, useMobileOrderCardLayout } from './MobileOrderCard';
import { MobileOrderDetailFooter } from './MobileOrderDetailFooter';
import { MobileOrderSettingsToggles } from './MobileOrderSettingsToggles';
import { renderOrderWarningDetails } from './mobileOrderWarnings';

interface MobileFilteredOrdersProps {
  blotterID: string;
  tabLabel?: string;
  defaultFilter?: BlotterTableOrderFilter;
  defaultColumns?: Parameters<typeof useFilteredOrdersBlotter>[0]['defaultColumns'];
  alwaysShowDDHOrders?: boolean;
}

/**
 * Mobile counterpart to desktop `FilteredOrders`. Reuses the same data observable,
 * column definitions, and filter logic — only the rendering layer changes
 * (`MobileDataTable` + `MobileOrderCard` instead of `BlotterTable`).
 *
 * The mobile header exposes filters through a modal sheet instead of the
 * desktop accordion. Summary line and export UI are still skipped.
 *
 * Row taps open a {@link MobileRowDetailSheet} via the default
 * {@link MobileDataTable} behaviour — no explicit `onRowPress` needed.
 */
export function MobileFilteredOrders({
  blotterID,
  tabLabel,
  defaultFilter,
  defaultColumns,
  alwaysShowDDHOrders,
}: MobileFilteredOrdersProps) {
  const {
    density,
    rowID,
    columns,
    persisted,
    filteredOrders,
    dataObservable,
    errorObservable,
    limitReachedState,
    raisePaginationLimits,
    exportOptions,
  } = useFilteredOrdersBlotter({
    blotterID,
    tabLabel,
    defaultFilter,
    defaultColumns,
    alwaysShowDDHOrders,
  });

  const blotterTable = useBlotterTable<Order>({
    dataObservable,
    errorObservable,
    rowID,
    density,
    clientLocalFilter: filteredOrders.clientSideFilter,
    columns,
    persistence: persisted,
    exportOptions,
  });

  const filterBuilderAccordion = useAccordionFilterBuilder({
    filterBuilderProps: filteredOrders.filterBuilderProps,
  });

  const dateRangeFilter = useDateRangeFilter(filteredOrders.filter, filteredOrders.changeFilter);

  const rowDetailFooter = useCallback((data: Order) => <MobileOrderDetailFooter order={data} />, []);
  const { cardHeight, cardRendererProps } = useMobileOrderCardLayout();

  return (
    <>
      <MobileBlotterToolbar
        {...filterBuilderAccordion}
        {...dateRangeFilter}
        {...blotterTable.blotterTableFiltersProps}
        timestampFields={orderTimestampFields}
        settingsContent={
          <MobileOrderSettingsToggles
            filter={filteredOrders.filter}
            changeFilter={filteredOrders.changeFilter}
            alwaysShowDDHOrders={alwaysShowDDHOrders}
          />
        }
      />
      <MaxRecordsReachedWarning
        showLimitReached={!!limitReachedState?.lastRecord}
        earliestTimestamp={limitReachedState?.lastRecord?.Timestamp}
        recordsReceived={limitReachedState?.recordsReceived}
        onRaiseLimitClicked={raisePaginationLimits}
      />
      <MobileDataTable<Order>
        {...blotterTable}
        cardRenderer={MobileOrderCard}
        cardRendererProps={cardRendererProps}
        cardHeight={cardHeight}
        detailsTitle="Order Details"
        rowDetailFooter={rowDetailFooter}
        rowDetailHeader={renderOrderWarningDetails}
      />
    </>
  );
}
